import type { ColumnFiltersState, OnChangeFn, RowData, SortingState } from '@tanstack/react-table';
import { type ColumnDef, type Row, useAppTable } from './appTable';
import { useTableStore } from './store/Context';
import type { ServerSideProps } from './types';

type UseTableInstanceOptions<T extends RowData> = {
  columns: ColumnDef<T>[];
  data: T[];
  enableRowSelection?: boolean;
  getRowCanSelect?: (row: Row<T>) => boolean;
  getRowId?: (row: T) => string;
  hasExpandableRows?: boolean;
  hideControls?: boolean;
  serverSide?: ServerSideProps;
};

export const useTableInstance = <T extends RowData>({
  columns,
  data,
  enableRowSelection,
  getRowCanSelect,
  getRowId,
  hasExpandableRows,
  hideControls,
  serverSide,
}: UseTableInstanceOptions<T>) => {
  const {
    columnOrder,
    columnSizing,
    columnVisibility,
    expanded,
    filters,
    pagination,
    resetTable,
    rowSelection,
    setColumnOrder,
    setColumnSizing,
    setColumnVisibility,
    setExpanded,
    setFilters,
    setPagination,
    setRowSelection,
    setSorting,
    sorting,
  } = useTableStore((s) => s);

  const isServerSide = !!serverSide;

  // Sorting and filter changes reset pagination to page 0 in server-side mode.
  // All other handlers pass the updater directly to the store, which resolves
  // it against the latest state inside Zustand's set().
  const onSortingChange: OnChangeFn<SortingState> = (updater) => {
    setSorting(updater);
    if (isServerSide) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  };
  const onColumnFiltersChange: OnChangeFn<ColumnFiltersState> = (updater) => {
    setFilters(updater);
    if (isServerSide) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  };

  const table = useAppTable({
    columnResizeDirection: 'ltr',
    columnResizeMode: 'onChange',
    columns,
    data,
    defaultColumn: { minSize: 96, size: 208 },
    enableMultiSort: true,
    enableRowSelection: enableRowSelection ? (getRowCanSelect ?? true) : false,
    enableSortingRemoval: false,
    ...(getRowId && { getRowId }),
    ...(hasExpandableRows && { getRowCanExpand: () => true }),
    // Server-side configuration
    ...(serverSide
      ? {
          manualPagination: true,
          manualSorting: true,
          manualFiltering: true,
          pageCount: serverSide.pageCount,
          rowCount: serverSide.rowCount,
        }
      : {}),
    onColumnFiltersChange,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    onColumnVisibilityChange: setColumnVisibility,
    onExpandedChange: setExpanded,
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    onSortingChange,
    state: {
      columnFilters: filters,
      columnOrder,
      columnSizing,
      columnVisibility,
      expanded,
      pagination: hideControls ? { pageIndex: 0, pageSize: data.length || 1 } : pagination,
      rowSelection,
      sorting,
    },
  });

  return {
    table,
    columnOrder,
    columnVisibility,
    pagination,
    resetTable,
    rowSelection,
    setColumnOrder,
    setColumnVisibility,
    setPagination,
    setRowSelection,
  };
};
